import { FEED_DEFAULT_RPM, FEED_MAX_RPM, type ApiKeyPublic } from '@carsfinders/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { useState } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { toast } from '../../lib/toast';
import {
  FEED_OPENAPI_URL,
  fetchFeedKeys,
  issueFeedKey,
  revokeFeedKey,
  type IssueFeedKeyResponse,
} from './api';

const keyId = (key: ApiKeyPublic): string => String((key as { _id?: unknown })._id ?? '');
const isActive = (key: ApiKeyPublic): boolean => key.active !== false && !key.revokedAt;

/**
 * Feed keys — `Redesign-D-Admin` / `-Mobile`, screen «API ключі».
 *
 * The artboard's table has six columns; five of them map onto what
 * `GET /admin/feed/keys` actually returns. The sixth does not: the artboard
 * prints an OBSERVED daily request count («Запитів / добу», e.g. «12 400») and
 * the API stores only the CONFIGURED per-minute ceiling (`rpm`). Printing 60
 * under a "per day" heading would misreport a limit as a measurement, so the
 * column keeps its geometry and says what it holds — «Ліміт запитів», «60/хв».
 *
 * The one-time secret stays one-time: `issueFeedKey` is the only moment the raw
 * key exists client-side, the reveal card is the only place it is rendered, and
 * dismissing it drops the value. Nothing re-displays it, by construction.
 */
export function FeedKeysPage() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [owner, setOwner] = useState('');
  const [label, setLabel] = useState('');
  const [rpm, setRpm] = useState<number>(FEED_DEFAULT_RPM);
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  /** The key awaiting the operator's «Відкликати» confirmation (BUG-51). */
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyPublic | null>(null);

  const keysQuery = useQuery({ queryKey: ['feed-keys'], queryFn: fetchFeedKeys });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['feed-keys'] });

  const issueMutation = useMutation({
    mutationFn: () => issueFeedKey({ owner: owner.trim(), label: label.trim() || undefined, rpm }),
    onSuccess: (data: IssueFeedKeyResponse) => {
      setRawKey(data.apiKey);
      setOwner('');
      setLabel('');
      setRpm(FEED_DEFAULT_RPM);
      setFormOpen(false);
      void invalidate();
    },
    onError: () => toast.error(t('feedKeys.issue.error')),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeFeedKey(id),
    onSuccess: () => void invalidate(),
    onError: () => toast.error(t('feedKeys.errors.revokeFailed')),
    onSettled: () => setRevokeTarget(null),
  });

  const handleCopy = () => {
    if (!rawKey || !navigator.clipboard) return;
    void navigator.clipboard.writeText(rawKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const error = keysQuery.error;
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return <AccessDenied />;
  }

  const keys = keysQuery.data?.keys ?? [];
  const activeCount = keys.filter(isActive).length;
  const revokedCount = keys.length - activeCount;

  return (
    <>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
            {t('feedKeys.headline')}
          </h1>
          <p className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:tracking-[0.14em]">
            <span className="lg:hidden">
              {t('feedKeys.eyebrowShort', { active: activeCount, revoked: revokedCount })}
            </span>
            <span className="hidden lg:inline">
              {t('feedKeys.eyebrow', { active: activeCount, revoked: revokedCount })}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen((open) => !open)}
          className="h-10 rounded-cf-md bg-cf-brand px-4 text-sm font-semibold text-white hover:bg-cf-brand-hover"
        >
          {formOpen ? t('feedKeys.cancel') : t('feedKeys.new')}
        </button>
      </div>

      {/* ── Issue form ──────────────────────────────────────────────────── */}
      {formOpen && (
        <section className="rounded-cf-lg border border-cf-line bg-cf-surface p-4 lg:px-6 lg:py-[22px]">
          <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[18px]">
            {t('feedKeys.issue.title')}
          </h2>
          <form
            className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (owner.trim().length === 0) return;
              issueMutation.mutate();
            }}
          >
            <Field id="feed-owner" label={t('feedKeys.issue.owner')}>
              <input
                id="feed-owner"
                value={owner}
                onChange={(event) => setOwner(event.target.value)}
                placeholder={t('feedKeys.issue.ownerPlaceholder')}
                className={INPUT_CLASS}
                required
              />
            </Field>
            <Field id="feed-label" label={t('feedKeys.issue.label')}>
              <input
                id="feed-label"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder={t('feedKeys.issue.labelPlaceholder')}
                className={INPUT_CLASS}
              />
            </Field>
            <Field id="feed-rpm" label={t('feedKeys.issue.rpm')}>
              <input
                id="feed-rpm"
                type="number"
                min={1}
                max={FEED_MAX_RPM}
                value={rpm}
                onChange={(event) => setRpm(Number(event.target.value))}
                className={`${INPUT_CLASS} font-mono`}
              />
            </Field>
            <div className="sm:col-span-3">
              <button
                type="submit"
                disabled={issueMutation.isPending || owner.trim().length === 0}
                className="h-11 rounded-cf-md bg-cf-brand px-4 text-sm font-semibold text-white hover:bg-cf-brand-hover disabled:opacity-50"
              >
                {issueMutation.isPending
                  ? t('feedKeys.issue.submitting')
                  : t('feedKeys.issue.submit')}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* ── The raw key, shown ONCE ─────────────────────────────────────── */}
      {rawKey && (
        <section className="rounded-cf-lg border border-cf-line bg-cf-surface p-4 shadow-[inset_0_3px_0_var(--color-cf-accent)] lg:px-6 lg:py-[22px]">
          <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[18px]">
            {t('feedKeys.raw.title')}
          </h2>
          <p className="mt-1.5 text-[13px] leading-[1.5] text-cf-ink-muted">
            {t('feedKeys.raw.warning')}
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <code className="min-w-0 flex-1 overflow-x-auto rounded-cf-sm bg-cf-ground px-3 py-2 font-mono text-[13px] whitespace-nowrap text-cf-ink-muted">
              {rawKey}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              className="flex h-10 shrink-0 items-center gap-2 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-sm font-semibold hover:border-cf-ink"
            >
              {copied ? <Check size={15} strokeWidth={1.75} /> : <Copy size={15} strokeWidth={1.75} />}
              {copied ? t('feedKeys.raw.copied') : t('feedKeys.raw.copy')}
            </button>
            <button
              type="button"
              onClick={() => setRawKey(null)}
              className="h-10 shrink-0 rounded-cf-md bg-cf-brand px-3.5 text-sm font-semibold text-white hover:bg-cf-brand-hover"
            >
              {t('feedKeys.raw.done')}
            </button>
          </div>
        </section>
      )}

      {/* ── Key table ───────────────────────────────────────────────────── */}
      {keysQuery.isLoading ? (
        <Placeholder text={t('feedKeys.loading')} />
      ) : keysQuery.isError ? (
        <Placeholder text={t('feedKeys.errors.failedToLoad')} />
      ) : keys.length === 0 ? (
        <Placeholder text={t('feedKeys.empty')} />
      ) : (
        <section className="flex flex-col gap-2 lg:block lg:gap-0 lg:overflow-x-auto lg:rounded-cf-lg lg:border lg:border-cf-line lg:bg-cf-surface">
          <div className={`hidden ${GRID} lg:grid lg:border-b lg:border-cf-line-soft lg:py-2.5 lg:font-mono lg:text-[10px] lg:tracking-[0.12em] lg:text-cf-ink-subtle lg:uppercase`}>
            <span>{t('feedKeys.table.partner')}</span>
            <span>{t('feedKeys.table.prefix')}</span>
            <span>{t('feedKeys.table.rateLimit')}</span>
            <span>{t('feedKeys.table.lastCall')}</span>
            <span>{t('feedKeys.table.status')}</span>
            <span />
          </div>

          {keys.map((key) => (
            <KeyRow
              key={keyId(key)}
              apiKey={key}
              revoking={revokeMutation.isPending}
              onRevoke={() => setRevokeTarget(key)}
            />
          ))}
        </section>
      )}

      {/* BUG-51: the same in-app confirmation every other irreversible admin action uses. */}
      <ConfirmDialog
        open={revokeTarget !== null}
        tone="danger"
        title={t('feedKeys.table.confirmRevokeTitle')}
        message={t('feedKeys.table.confirmRevoke')}
        confirmLabel={t('feedKeys.table.revoke')}
        busy={revokeMutation.isPending}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={() => {
          if (revokeTarget) revokeMutation.mutate(keyId(revokeTarget));
        }}
      />

      {/* ── Partner docs ────────────────────────────────────────────────── */}
      <a
        href={FEED_OPENAPI_URL}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2 text-[13px] font-semibold text-cf-brand"
      >
        <ExternalLink size={15} strokeWidth={1.75} />
        {t('feedKeys.docs.link')}
      </a>
    </>
  );
}

const GRID =
  'lg:grid lg:min-w-[900px] lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_120px_130px_110px_110px] lg:items-center lg:gap-4 lg:px-[18px]';

const INPUT_CLASS =
  'h-10 w-full rounded-cf-md border border-cf-line bg-cf-surface px-3 text-sm placeholder:text-cf-ink-subtle focus:border-cf-ink focus:outline-none';

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function KeyRow({
  apiKey,
  revoking,
  onRevoke,
}: {
  apiKey: ApiKeyPublic;
  revoking: boolean;
  onRevoke: () => void;
}) {
  const active = isActive(apiKey);
  const scope = apiKey.label ?? t('feedKeys.table.noScope');
  const masked = `${apiKey.keyPrefix}…`;
  const rate = t('feedKeys.table.rateLimitValue', { rpm: apiKey.rpm });
  const lastCall = apiKey.lastUsedAt
    ? new Intl.DateTimeFormat('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(
        new Date(apiKey.lastUsedAt),
      )
    : t('feedKeys.table.never');
  const status = active ? t('feedKeys.table.active') : t('feedKeys.table.revoked');
  const badge = active ? 'bg-cf-success-tint text-cf-success' : 'bg-cf-fill text-cf-ink-subtle';

  return (
    <>
      {/* ── Mobile card ───────────────────────────────────────────────── */}
      <div className="flex items-center gap-2.5 rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 lg:hidden">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{apiKey.owner}</div>
          <div className="truncate font-mono text-xs text-cf-ink-subtle">
            {masked} · {rate} · {lastCall}
          </div>
        </div>
        <span
          className={`shrink-0 rounded-cf-sm px-[7px] py-[3px] font-mono text-[9px] tracking-[0.08em] whitespace-nowrap uppercase ${badge}`}
        >
          {status}
        </span>
        {active && (
          <button
            type="button"
            onClick={onRevoke}
            disabled={revoking}
            className="h-8 shrink-0 rounded-cf-md border border-cf-line px-2.5 text-xs font-semibold text-cf-danger disabled:opacity-50"
          >
            {revoking ? t('feedKeys.table.revoking') : t('feedKeys.table.revoke')}
          </button>
        )}
      </div>

      {/* ── Desktop row ───────────────────────────────────────────────── */}
      <div className={`hidden ${GRID} lg:border-b lg:border-cf-line-soft lg:py-3 lg:text-[13px]`}>
        <div className="min-w-0">
          <div className="truncate font-semibold">{apiKey.owner}</div>
          <div className="truncate text-xs text-cf-ink-subtle">{scope}</div>
        </div>

        <code className="truncate rounded-cf-sm bg-cf-ground px-2 py-1 font-mono text-xs text-cf-ink-muted">
          {masked}
        </code>

        <span className="font-mono">{rate}</span>

        <span className="font-mono text-xs text-cf-ink-subtle">{lastCall}</span>

        <span
          className={`inline-flex w-fit rounded-cf-sm px-2 py-1 font-mono text-[10px] tracking-[0.08em] uppercase ${badge}`}
        >
          {status}
        </span>

        {active ? (
          <button
            type="button"
            onClick={onRevoke}
            disabled={revoking}
            className="h-8 justify-self-end rounded-cf-md border border-cf-line bg-cf-surface px-2.5 text-xs font-semibold text-cf-danger hover:border-cf-ink disabled:opacity-50"
          >
            {revoking ? t('feedKeys.table.revoking') : t('feedKeys.table.revoke')}
          </button>
        ) : (
          <span />
        )}
      </div>
    </>
  );
}

function Placeholder({ text }: { text: string }) {
  return (
    <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-10 text-center text-[13px] text-cf-ink-subtle lg:px-[18px]">
      {text}
    </section>
  );
}
